import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import api from '../utils/api';
import { formatCurrency, formatNumber, formatDate, getDatePreset } from '../utils/formatters';
import DateRangeFilter from '../components/DateRangeFilter';
import KPICard from '../components/KPICard';
import ChartCard from '../components/ChartCard';

const PAGE_SIZES = [10, 20, 50, 100, 'all'];

// TikTok hands back rates already multiplied out, so these just get a unit.
const pct = (v) => (v == null ? '--' : `${Number(v).toFixed(2)}%`);
const money = (v) => (v == null ? '--' : formatCurrency(v));
const count = (v) => (v == null ? '--' : formatNumber(v));

// Session length reads better in hours and minutes than as a decimal.
function fmtDuration(seconds) {
  if (!seconds) return '--';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
}

// Average watch time is tens of seconds to a few minutes, so m:ss is the
// readable unit here rather than the h/m used for session length.
function fmtWatch(seconds) {
  if (!seconds) return '--';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// start_time / end_time are stored as Eastern "YYYY-MM-DDTHH:MM:SS" strings, so
// the clock portion is already correct — parsing to a Date would re-apply the
// browser's zone and shift it.
const timeOnly = (ts) => (ts ? ts.slice(11, 16) : '--');

const ALL_COLUMNS = [
  { key: 'start_time',           label: 'Date',        align: 'left',  type: 'date',   fmt: (v) => formatDate(v) },
  { key: 'title',                label: 'Title',       align: 'left',  type: 'string' },
  { key: 'username',             label: 'Host',        align: 'left',  type: 'string' },
  { key: 'start_time_clock',     label: 'Start',       align: 'right', type: 'string', get: (r) => timeOnly(r.start_time) },
  { key: 'end_time_clock',       label: 'End',         align: 'right', type: 'string', get: (r) => timeOnly(r.end_time) },
  { key: 'duration_seconds',     label: 'Duration',    align: 'right', type: 'number', fmt: fmtDuration },
  { key: 'gmv',                  label: 'GMV',         align: 'right', type: 'number', fmt: money },
  { key: 'gmv_per_hour',         label: 'GMV/Hour',    align: 'right', type: 'number', fmt: money },
  { key: 'watch_gpm',            label: 'Watch GPM',   align: 'right', type: 'number', fmt: money },
  { key: 'sku_orders',           label: 'Orders',      align: 'right', type: 'number', fmt: count },
  { key: 'customers',            label: 'Customers',   align: 'right', type: 'number', fmt: count },
  { key: 'items_sold',           label: 'Items Sold',  align: 'right', type: 'number', fmt: count },
  { key: 'aov',                  label: 'AOV',         align: 'right', type: 'number', fmt: money },
  { key: 'views',                label: 'Views',       align: 'right', type: 'number', fmt: count },
  { key: 'viewers',              label: 'Viewers',     align: 'right', type: 'number', fmt: count },
  { key: 'pcu',                  label: 'Peak Concurrent', align: 'right', type: 'number', fmt: count },
  { key: 'avg_viewing_duration', label: 'Avg Watch',   align: 'right', type: 'number', fmt: fmtWatch },
  { key: 'likes',                label: 'Likes',       align: 'right', type: 'number', fmt: count },
  { key: 'comments',             label: 'Comments',    align: 'right', type: 'number', fmt: count },
  { key: 'shares',               label: 'Shares',      align: 'right', type: 'number', fmt: count },
  { key: 'new_followers',        label: 'New Followers', align: 'right', type: 'number', fmt: count },
  { key: 'product_clicks',       label: 'Product Clicks', align: 'right', type: 'number', fmt: count },
  { key: 'click_through_rate',   label: 'CTR',         align: 'right', type: 'number', fmt: pct },
];

// A sane default set — the rest are one click away in the column picker, so the
// table stays readable without hiding anything permanently.
const DEFAULT_COLUMNS = [
  'start_time', 'title', 'start_time_clock', 'duration_seconds', 'gmv', 'gmv_per_hour',
  'sku_orders', 'customers', 'items_sold', 'views', 'pcu', 'avg_viewing_duration', 'click_through_rate',
];

const cellValue = (row, col) => (col.get ? col.get(row) : row[col.key]);

function compareRows(a, b, col, dir) {
  const av = cellValue(a, col);
  const bv = cellValue(b, col);
  if (col.type === 'string') {
    return (av || '').localeCompare(bv || '', undefined, { sensitivity: 'base' }) * dir;
  }
  if (col.type === 'date') {
    return ((av ? new Date(av).getTime() : 0) - (bv ? new Date(bv).getTime() : 0)) * dir;
  }
  return ((av ?? 0) - (bv ?? 0)) * dir;
}

function ColumnPicker({ visible, onToggle }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button className="btn btn-secondary btn-sm" onClick={() => setOpen(o => !o)}>
        Columns ({visible.length})
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setOpen(false)} />
          <div className="card" style={{
            position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 11,
            width: 220, maxHeight: 320, overflowY: 'auto', padding: 12,
          }}>
            {ALL_COLUMNS.map(col => (
              <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={visible.includes(col.key)} onChange={() => onToggle(col.key)} />
                {col.label}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SessionsTable({ rows, visibleKeys, onToggleColumn, pageSize, onPageSizeChange, onRowClick }) {
  const [sortKey, setSortKey] = useState('start_time');
  const [sortDir, setSortDir] = useState('desc');

  const columns = useMemo(
    () => ALL_COLUMNS.filter(c => visibleKeys.includes(c.key)),
    [visibleKeys]
  );

  function toggleSort(col) {
    if (sortKey === col.key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(col.key); setSortDir(col.type === 'string' ? 'asc' : 'desc'); }
  }

  const sorted = useMemo(() => {
    const col = ALL_COLUMNS.find(c => c.key === sortKey) || columns[0];
    if (!col) return rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => compareRows(a, b, col, dir));
  }, [rows, columns, sortKey, sortDir]);

  const visible = pageSize === 'all' ? sorted : sorted.slice(0, pageSize);

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div className="card-title" style={{ margin: 0 }}>Sessions</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {pageSize === 'all' ? `${sorted.length}` : `${visible.length} of ${sorted.length}`}
          </span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
            Show:
            <select value={String(pageSize)}
                    onChange={e => onPageSizeChange(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                    style={{ width: 'auto' }}>
              {PAGE_SIZES.map(s => <option key={s} value={String(s)}>{s === 'all' ? 'All' : s}</option>)}
            </select>
          </label>
          <ColumnPicker visible={visibleKeys} onToggle={onToggleColumn} />
        </div>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'right', width: 40, color: 'var(--text-secondary)' }}>#</th>
              {columns.map(col => (
                <th key={col.key} onClick={() => toggleSort(col)}
                    style={{ textAlign: col.align, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                  {col.label} {sortKey === col.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={columns.length + 1} style={{ textAlign: 'center', color: 'var(--text-light)', padding: 24 }}>
                No live sessions in this date range.
              </td></tr>
            )}
            {visible.map((row, i) => (
              <tr key={row.live_id} onClick={() => onRowClick(row)} style={{ cursor: 'pointer' }}>
                <td style={{ textAlign: 'right', color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
                {columns.map(col => (
                  <td key={col.key} style={{ textAlign: col.align }}>
                    {col.fmt ? col.fmt(cellValue(row, col)) : (cellValue(row, col) ?? '--')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// "vs last N streams" — shown only when there is a baseline to compare against,
// so a shop's first few sessions don't render meaningless deltas.
function TrailingDelta({ value, baseline }) {
  if (value == null || baseline == null || baseline === 0) return null;
  const delta = ((value - baseline) / baseline) * 100;
  const positive = delta >= 0;
  return (
    <span style={{ fontSize: 12, fontWeight: 500, color: positive ? 'var(--success, #16a34a)' : 'var(--danger, #dc2626)' }}>
      {positive ? '+' : ''}{delta.toFixed(0)}%
    </span>
  );
}

function FunnelRow({ label, value, sub }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
        {sub && <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8 }}>{sub}</span>}
      </span>
    </div>
  );
}

function SessionDetail({ session, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minutes, setMinutes] = useState(null);
  const [minutesState, setMinutesState] = useState('idle');
  const [minutesError, setMinutesError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/reports/live-sessions/${session.live_id}`)
      .then(({ data }) => setDetail(data))
      .catch(err => console.error('Live session detail error:', err))
      .finally(() => setLoading(false));
  }, [session.live_id]);

  async function loadMinutes() {
    setMinutesState('loading');
    setMinutesError(null);
    try {
      const { data } = await api.get(`/api/reports/live-sessions/${session.live_id}/minutes`);
      setMinutes((data.intervals || []).map(iv => ({
        time: new Date(iv.start_time * 1000).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' }),
        gmv: parseFloat(iv.sales?.gmv?.amount || 0),
        viewers: parseInt(iv.traffic?.viewers || 0),
        comments: parseInt(iv.interactions?.comments || 0),
      })));
      setMinutesState('loaded');
    } catch (err) {
      setMinutesError(err?.response?.data?.error || 'Could not load minute-by-minute data.');
      setMinutesState('error');
    }
  }

  const s = detail?.session || session;
  const t = detail?.trailing;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '5vh', overflowY: 'auto' }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card" style={{ width: '100%', maxWidth: 1000, margin: '0 16px 40px', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 16, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>

        <h3 className="card-title" style={{ marginBottom: 4, paddingRight: 32 }}>{s.title || 'Untitled LIVE'}</h3>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
          {s.username ? `@${s.username} · ` : ''}{formatDate(s.start_time)} · {timeOnly(s.start_time)}–{timeOnly(s.end_time)} · {fmtDuration(s.duration_seconds)}
        </div>

        {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}><div className="spinner" /></div>}

        {!loading && (
          <>
            <div className="kpi-grid" style={{ marginBottom: 20 }}>
              <KPICard label="GMV" value={money(s.gmv)} secondary={t && <TrailingDelta value={s.gmv} baseline={t.gmv} />}
                       info="TikTok's GMV for this session. Tax-inclusive and includes returns and refunds, so it will not tie exactly to the Financials GMV card." />
              <KPICard label="GMV per Hour" value={money(s.gmv_per_hour)} secondary={t && <TrailingDelta value={s.gmv_per_hour} baseline={t.gmv_per_hour} />}
                       info="Session GMV divided by hours streamed." />
              <KPICard label="Watch GPM" value={money(s.watch_gpm)} info="GMV generated per 1,000 views." />
              <KPICard label="Paid Orders" value={count(s.sku_orders)} />
              <KPICard label="Customers" value={count(s.customers)} info="Unique buyers who ordered from this LIVE." />
              <KPICard label="Items Sold" value={count(s.items_sold)} />
              <KPICard label="AOV" value={money(s.aov)} info="Session GMV divided by paid orders." />
              <KPICard label="Viewers" value={count(s.viewers)} secondary={t && <TrailingDelta value={s.viewers} baseline={t.viewers} />}
                       info="Unique viewers. Views counts repeat entries into the room." />
              <KPICard label="Peak Concurrent" value={count(s.pcu)} />
              <KPICard label="Avg Watch Time" value={fmtWatch(s.avg_viewing_duration)} />
              <KPICard label="New Followers" value={count(s.new_followers)} />
              <KPICard label="Likes / Comments / Shares" value={`${count(s.likes)} / ${count(s.comments)} / ${count(s.shares)}`} />
            </div>

            {t && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 20 }}>
                Percentages compare against the average of the previous {t.count} session{t.count === 1 ? '' : 's'}.
              </div>
            )}

            {detail?.break && (
              <div className="card" style={{ padding: 16, marginBottom: 20 }}>
                <div className="card-title" style={{ marginBottom: 8 }}>Linked Break</div>
                <div style={{ fontSize: 14 }}>
                  {detail.break.break_number ? `#${detail.break.break_number} ` : ''}{detail.break.name}
                  <span style={{ color: 'var(--text-secondary)' }}> · {detail.break.category || 'Uncategorised'} · {detail.break.status}</span>
                </div>
                <div style={{ display: 'flex', gap: 24, marginTop: 10, fontVariantNumeric: 'tabular-nums' }}>
                  <div><div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Break COGS</div>{money(detail.break.cogs)}</div>
                  <div><div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Session GMV − COGS</div>{money(s.gmv - detail.break.cogs)}</div>
                </div>
              </div>
            )}

            <div className="card" style={{ padding: 16, marginBottom: 20 }}>
              <div className="card-title" style={{ marginBottom: 4 }}>Conversion Funnel</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                Where a session lost people — traffic problem or closing problem.
              </div>
              <FunnelRow label="Views" value={count(s.views)} />
              <FunnelRow label="Unique viewers" value={count(s.viewers)} />
              <FunnelRow label="Product impressions" value={count(s.product_impressions)} />
              <FunnelRow label="Product clicks" value={count(s.product_clicks)} sub={`CTR ${pct(s.click_through_rate)}`} />
              <FunnelRow label="Paid orders" value={count(s.sku_orders)} sub={`Click to order ${pct(s.click_to_order_rate)}`} />
            </div>

            <div className="card" style={{ padding: 16, marginBottom: 20 }}>
              <div className="card-title" style={{ marginBottom: 12 }}>Products Sold</div>
              {detail?.productsError && (
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Could not load product breakdown: {detail.productsError}
                </div>
              )}
              {!detail?.productsError && (!detail?.products || detail.products.length === 0) && (
                <div style={{ fontSize: 13, color: 'var(--text-light)' }}>No product data for this session.</div>
              )}
              {detail?.products?.length > 0 && (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Product</th>
                        <th style={{ textAlign: 'right' }}>GMV</th>
                        <th style={{ textAlign: 'right' }}>Items</th>
                        <th style={{ textAlign: 'right' }}>Orders</th>
                        <th style={{ textAlign: 'right' }}>Customers</th>
                        <th style={{ textAlign: 'right' }}>Clicks</th>
                        <th style={{ textAlign: 'right' }}>CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.products.map(p => (
                        <tr key={p.product_id}>
                          <td>{p.product_name || p.product_id}</td>
                          <td style={{ textAlign: 'right' }}>{money(p.direct_gmv)}</td>
                          <td style={{ textAlign: 'right' }}>{count(p.items_sold)}</td>
                          <td style={{ textAlign: 'right' }}>{count(p.sku_orders)}</td>
                          <td style={{ textAlign: 'right' }}>{count(p.customers)}</td>
                          <td style={{ textAlign: 'right' }}>{count(p.product_clicks)}</td>
                          <td style={{ textAlign: 'right' }}>{pct(p.ctr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div className="card-title" style={{ margin: 0 }}>Minute by Minute</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    Available once a session has ended. Loaded on request — it is a large response.
                  </div>
                </div>
                {minutesState !== 'loaded' && (
                  <button className="btn btn-secondary btn-sm" onClick={loadMinutes} disabled={minutesState === 'loading'}>
                    {minutesState === 'loading' ? 'Loading…' : 'Load replay'}
                  </button>
                )}
              </div>
              {minutesState === 'error' && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{minutesError}</div>}
              {minutesState === 'loaded' && minutes?.length > 0 && (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={minutes} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="time" tick={{ fontSize: 11 }} minTickGap={40} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="gmv" name="GMV" stroke="#2563eb" dot={false} />
                    <Line yAxisId="right" type="monotone" dataKey="viewers" name="Viewers" stroke="#16a34a" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
              {minutesState === 'loaded' && (!minutes || minutes.length === 0) && (
                <div style={{ fontSize: 13, color: 'var(--text-light)' }}>No minute-level data returned for this session.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function LiveSessions() {
  const [dateRange, setDateRange] = useState(getDatePreset('last_30d'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(20);
  const [visibleKeys, setVisibleKeys] = useState(DEFAULT_COLUMNS);
  const [selected, setSelected] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/api/reports/live-sessions', { params: dateRange });
      setData(data);
    } catch (err) {
      console.error('Live sessions fetch error:', err);
      setError(err?.response?.data?.error || 'Failed to load live sessions.');
    }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function toggleColumn(key) {
    setVisibleKeys(keys => (keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key]));
  }

  const totals = data?.totals;
  const chartData = useMemo(() => {
    if (!data?.sessions) return [];
    return [...data.sessions]
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
      .map(s => ({ label: `${formatDate(s.start_time)} ${timeOnly(s.start_time)}`, gmv: s.gmv, gmvPerHour: s.gmv_per_hour }));
  }, [data]);

  return (
    <div>
      <h2 className="page-title" style={{ marginBottom: 20 }}>Live Sessions</h2>
      <DateRangeFilter onChange={setDateRange} initial="last_30d" />

      {data?.latestAvailableDate && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
          TikTok LIVE analytics are complete through <strong>{formatDate(data.latestAvailableDate)}</strong>. Anything after that date may still be revised.
        </div>
      )}

      {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><div className="spinner" /></div>}
      {!loading && error && <div className="card" style={{ marginTop: 16, color: 'var(--text-secondary)' }}>{error}</div>}

      {!loading && !error && (
        <>
          <div className="kpi-grid" style={{ marginTop: 16 }}>
            <KPICard label="Sessions" value={count(totals?.sessions)} />
            <KPICard label="Live GMV" value={money(totals?.gmv)}
                     info="TikTok's own GMV for these sessions. Tax-inclusive and includes returns and refunds, so it will not tie exactly to the Financials GMV card." />
            <KPICard label="Orders" value={count(totals?.orders)} />
            <KPICard label="Customers" value={count(totals?.customers)} />
            <KPICard label="Items Sold" value={count(totals?.itemsSold)} />
            <KPICard label="Hours Streamed" value={totals?.hoursStreamed ? totals.hoursStreamed.toFixed(1) : '--'} />
            <KPICard label="GMV per Hour" value={money(totals?.gmvPerHour)} info="Total live GMV divided by total hours streamed." />
            <KPICard label="Watch GPM" value={money(totals?.watchGpm)} info="GMV generated per 1,000 views across these sessions." />
          </div>

          {chartData.length > 1 && (
            <ChartCard title="GMV by Session">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={30} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="gmv" name="GMV" stroke="#2563eb" dot={false} />
                  <Line type="monotone" dataKey="gmvPerHour" name="GMV/Hour" stroke="#f59e0b" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          <SessionsTable
            rows={data?.sessions || []}
            visibleKeys={visibleKeys}
            onToggleColumn={toggleColumn}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            onRowClick={setSelected}
          />
        </>
      )}

      {selected && <SessionDetail session={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
